//frontend/src/components/cart/Payment.js
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { clearCart } from '../../slices/cartSlice';
import {
    CreditCard,
    Loader2,
    IndianRupee,
    ShieldCheck,
    LockIcon,
    ShoppingBag,
    ArrowLeft
} from 'lucide-react';
import MetaData from '../layouts/MetaData';
import CheckoutSteps from './CheckoutStep';

const Payment = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const payBtn = useRef(null);
    const [loading, setLoading] = useState(false);
    const [sdkReady, setSdkReady] = useState(false);
    const { shippingInfo } = useSelector(state => state.cartState);
    const { user, isAuthenticated } = useSelector(state => state.authState);
    const [orderInfo, setOrderInfo] = useState(null);

    useEffect(() => {
        const storedOrderInfo = sessionStorage.getItem('orderInfo');
        if (!storedOrderInfo) {
            navigate('/cart');
            return;
        }

        setOrderInfo(JSON.parse(storedOrderInfo));

        // Load Razorpay SDK
        const loadRazorpayScript = async () => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => setSdkReady(true);
            document.body.appendChild(script);
        };

        if (!window.Razorpay) {
            loadRazorpayScript();
        } else {
            setSdkReady(true);
        }
    }, [navigate]);

    const handlePaymentSuccess = async (response) => {
        try {
            setLoading(true);
            const storedOrderInfo = JSON.parse(sessionStorage.getItem('orderInfo'));

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'x-guest-checkout': !isAuthenticated ? 'true' : 'false'
                }
            };

            // Verify payment
            await axios.post('/api/v1/payment/verify', {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
            }, config);

            const orderData = {
                shippingInfo: storedOrderInfo.shippingInfo,
                orderItems: storedOrderInfo.orderItems,
                itemsPrice: storedOrderInfo.itemsPrice,
                shippingPrice: storedOrderInfo.shippingPrice,
                totalPrice: storedOrderInfo.totalPrice,
                paymentInfo: {
                    id: response.razorpay_payment_id,
                    status: 'succeeded',
                    paymentMethod: 'Online'
                },
                guestInfo: !isAuthenticated ? storedOrderInfo.guestInfo : undefined
            };

            const { data } = await axios.post('/api/v1/order/new', orderData, config);

            if (data.success) {
                navigate('/success', {
                    replace: true,
                    state: {
                        orderInfo: {
                            ...orderData,
                            orderId: data.order.orderId,
                            createdAt: new Date().toISOString()
                        }
                    }
                });

                setTimeout(() => {
                    dispatch(clearCart());
                    localStorage.removeItem('cartItems');
                    sessionStorage.removeItem('orderInfo');
                    if (!isAuthenticated) {
                        localStorage.removeItem('guestInfo');
                        localStorage.removeItem('isGuestCheckout');
                    }
                }, 100);
            }
        } catch (error) {
            console.error('Payment processing error:', error);
            toast.error(error.response?.data?.message || 'Payment processing failed');
            navigate('/cart');
        } finally {
            setLoading(false);
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();
        
        if (!sdkReady) {
            toast.error('Payment system is initializing. Please wait.');
            return;
        }

        if (!orderInfo) {
            toast.error('Order information not found');
            navigate('/cart');
            return;
        }

        try {
            setLoading(true);
            payBtn.current.disabled = true;

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'x-guest-checkout': !isAuthenticated ? 'true' : 'false'
                }
            };

            const { data: { razorpayKeyId } } = await axios.get('/api/v1/razorpaykey', config);

            // Add these debug logs:
console.log('Razorpay key being used:', razorpayKeyId);
console.log('Is live key?', razorpayKeyId.startsWith('rzp_live_'));

            const { data: { order } } = await axios.post('/api/v1/payment/process', {
                amount: orderInfo.totalPrice
            }, config);

            const options = {
                key: razorpayKeyId,
                amount: order.amount,
                currency: "INR",
                name: 'Meat Buddy',
                description: 'Payment for your order',
                order_id: order.id,
                prefill: {
                    name: isAuthenticated ? user?.name : orderInfo.guestInfo?.name,
                    email: isAuthenticated ? user?.email : orderInfo.guestInfo?.email,
                    contact: shippingInfo.phoneNo
                },
                notes: {
                    address: shippingInfo.address
                },
                theme: {
                    color: '#dc2626'
                },
                handler: (response) => handlePaymentSuccess(response, config)
            };

            const razorpay = new window.Razorpay(options);
            
            razorpay.on('payment.failed', function(response) {
                console.error('Payment failed:', response);
                toast.error(response.error.description || 'Payment failed');
                payBtn.current.disabled = false;
                setLoading(false);
            });

            razorpay.open();
        } catch (error) {
            console.error('Payment initialization error:', error);
            console.error('Full error details:', {
                response: error.response,
                message: error.message,
                stack: error.stack
            });
            payBtn.current.disabled = false;
            setLoading(false);
            toast.error(error.response?.data?.message || 'Payment initialization failed');
        }
    };

    return (
        <>
            <MetaData title={'Payment'} />
            <div className="min-h-screen py-8" style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}>
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <CheckoutSteps shipping confirmOrder payment />
                    
                    <div className="mt-8">
                        <div className="rounded-xl shadow-sm overflow-hidden">
                            <div className="border-b border-gray-200 p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <CreditCard className="w-6 h-6 text-[#dc2626]" />
                                        <h1 className="text-2xl font-bold text-gray-900 font-['Crimson_Text']">
                                            Payment
                                        </h1>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-[#dc2626] hover:bg-gray-100 rounded-lg transition-colors font-medium"
                                    >
                                        <ArrowLeft className="w-5 h-5" />
                                        Go Back
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-6">
                                <div className="mb-8">
                                    <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
                                    <div className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
                                        <div>
                                            <p className="text-sm text-gray-500">Total Amount</p>
                                            <div className="flex items-center text-xl font-bold text-[#dc2626]">
                                                <IndianRupee className="w-5 h-5 mr-1" />
                                                {orderInfo?.totalPrice}
                                            </div>
                                        </div>
                                        <ShoppingBag className="w-6 h-6 text-gray-400" />
                                    </div>
                                </div>

                                <form onSubmit={handlePayment} className="space-y-6">
                                    <button
                                        ref={payBtn}
                                        type="submit"
                                        disabled={loading || !sdkReady}
                                        className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center
                                            ${loading || !sdkReady
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-[#dc2626] text-white hover:bg-[#dc2626]/90'
                                            } transition-colors`}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                Processing...
                                            </>
                                        ) : !sdkReady ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                Loading Payment System...
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="w-5 h-5 mr-2" />
                                                Pay ₹{orderInfo?.totalPrice}
                                            </>
                                        )}
                                    </button>
                                </form>

                                <div className="mt-8 space-y-4">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <ShieldCheck className="w-5 h-5 text-green-500" />
                                        <span>Your payment is secured by Razorpay</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <LockIcon className="w-5 h-5 text-[#dc2626]" />
                                        <span>256-bit SSL encryption for secure transactions</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Payment;